import { useTaxRates } from "@bcc/hooks/use-tax-rates";
import {
  DEFAULT_TAX_RATES_BY_REGION,
  homeRegionForVendor,
  resolveHomeRegionPricingContext,
  type TaxRatesByRegion,
} from "@bcc/lib/products/cross-border-tax";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import { CrossBorderCountriesSchema } from "@shared/schemas/enums/crossBorder.enum";
import { createContext, useContext, type ReactNode } from "react";

/**
 * Supplies per-region VAT/KS rates (from the tax-configuration API) to the
 * cross-border pricing UI. Rates are vendor-level — keyed on the vendor's region
 * and its cross-border ship-to countries — so they're fetched once here and
 * shared by every product row / variant.
 *
 * The context default is `DEFAULT_TAX_RATES_BY_REGION`, so consumers rendered
 * without the provider (e.g. isolated component tests/stories) get usable static
 * rates and never need their own QueryClient. Only this provider, mounted near
 * the app root beneath the QueryClient + VendorProvider, performs the fetch.
 */
const TaxRatesContext = createContext<TaxRatesByRegion>(DEFAULT_TAX_RATES_BY_REGION);

export const useTaxRatesContext = (): TaxRatesByRegion => useContext(TaxRatesContext);

export const TaxRatesProvider = ({ children }: { children: ReactNode }) => {
  const [{ currentVendor }] = useVendorContext();
  const crossBorderShipTo = (currentVendor?.properties?.shipTo ?? []).filter((country): country is CrossBorderCountries =>
    (CrossBorderCountriesSchema.options as readonly string[]).includes(country)
  );
  // DE-domestic vendors have no cross-border ship-to data at all, so
  // without this they'd never fetch live rates and would silently stay on
  // DEFAULT_TAX_RATES_BY_REGION. Widen the ship-to list the same way the
  // pricing UI does, so they get live rates too.
  const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
  const { shipTo } = resolveHomeRegionPricingContext(homeRegion, crossBorderShipTo);
  const { ratesByRegion } = useTaxRates(currentVendor?.region, shipTo);
  return <TaxRatesContext.Provider value={ratesByRegion}>{children}</TaxRatesContext.Provider>;
};
